import React, {useEffect, useState} from 'react'
import CreatableSelect from 'react-select/creatable'
import makeAnimated from 'react-select/animated'
import {ToolTipUIForModal} from '../UI/ToolTipUIForModal'

interface Option {
  id: number
  value: string
  label: string
}
interface TagInputProps {
  initialOptions: Option[]
  label: string
  placeholder: string
  tooltip: string
  fieldName?: string
  formik?: any
  disabled?: boolean
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  setDropdownOptions?: any
  getCategoryId?: any
}

const TagInput: React.FC<TagInputProps> = ({
  initialOptions,
  label,
  placeholder,
  tooltip,
  fieldName,
  formik,
  disabled = false,
  onBlur,
  getCategoryId,
}) => {
  const [options, setOptions] = useState<Option[]>(initialOptions)

  useEffect(() => {
    if (fieldName === 'categoryLists') {
      const validCategories = (formik.values.categoryLists || []).filter((selectedCategory: any) =>
        options.some((category) => category.id === selectedCategory.id)
      )

      if (validCategories.length !== formik.values.categoryLists?.length) {
        formik.setFieldValue('categoryLists', validCategories)
      }
    }
  }, [options]) // eslint-disable-line react-hooks/exhaustive-deps

  const animatedComponents = makeAnimated()
  const customStyles = {
    placeholder: (provided: any) => ({
      ...provided,
      fontSize: 13,
      fontWeight: 500,
    }),
    input: (provided: any) => ({
      ...provided,
      '&:focus': {
        boxShadow: 'none',
      },
      fontSize: 13,
      padding: 0,
    }),
    control: (baseStyles: any, state: any) => ({
      ...baseStyles,
      borderColor: state.isFocused ? '#b5b5c3' : '#e4e6ef',
      boxShadow: 'none',
      minHeight: '40px',
      maxHeight: '80px',
      overflowY: 'scroll',
    }),
    option: (styles: any, state: any) => ({
      ...styles,
      color: state.isSelected ? '#FFF' : styles.color,
      '&:hover': {
        color: '#4776E6',
        backgroundColor: '#E1E7ED',
      },
    }),
  }

  const handleCreateOption = (inputValue: string) => {
    const newId = options.length ? Math.max(...options.map((opt) => opt.id)) + 1 : 1
    const newOption: Option = {
      id: newId,
      value: inputValue.toLowerCase().replace(/\W/g, '_'),
      label: inputValue,
    }
    setOptions([...options, newOption])
    formik.setFieldValue(fieldName, [
      ...(formik?.values?.[fieldName as keyof typeof formik.values] || []),
      newOption,
    ])
  }

  const handleChange = (selectedOptions: any) => {
    formik.setFieldValue(fieldName, selectedOptions)
    const categoryIds = selectedOptions.map((option: any) => option.id)

    if (getCategoryId && categoryIds.length > 0) getCategoryId(categoryIds)
  }

  const filteredOptions = options?.filter((option) => {
    const selectedOptions = (formik?.values?.[fieldName as keyof typeof formik.values] ||
      []) as Option[]
    const selectedValues = selectedOptions?.map((selected: any) => selected?.value.trim())
    // Check if the option is the newly created option
    const isNewlyCreated = option.value === formik.values[fieldName!]?.[0]?.value

    return !selectedValues.includes(option.value.trim()) || isNewlyCreated
  })

  return (
    <>
      <div className='fv-row col-12 undefined mb-8 me-4'>
        <label className='form-label font-size-13 text-dark text-capitalize ms-md-0 d-flex flex-md-row align-items-md-center'>
          {label}
          <ToolTipUIForModal tooltipText={tooltip} />
        </label>
        <CreatableSelect
          isMulti
          closeMenuOnSelect={false}
          onBlur={onBlur}
          components={animatedComponents}
          defaultValue={formik?.values?.[fieldName as keyof typeof formik.values]}
          value={formik?.values?.[fieldName as keyof typeof formik.values] || []}
          name={fieldName}
          onChange={handleChange}
          isDisabled={disabled}
          options={filteredOptions}
          onCreateOption={handleCreateOption}
          formatCreateLabel={(inputValue) => `Create new "${inputValue}". Click or press enter.`}
          placeholder={placeholder}
          maxMenuHeight={200}
          classNames={{
            control: (state: any) => (state.isFocused ? 'border-red-600' : 'border-grey-300'),
          }}
          classNamePrefix='react-select'
          styles={customStyles}
          theme={(theme: any) => ({
            ...theme,
            border: 0,
            borderRadius: 5,
            colors: {
              ...theme.colors,
              primary25: '#E1E7ED',
              primary: '#e4e6ef',
            },
          })}
        />
      </div>
    </>
  )
}

export default TagInput
